import { useState } from "react";
import type { Appointment, ProcedureCode, StaffMember } from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import { editAppointment } from "../api/appointments";
import { Modal } from "../ui/modal";
import { Select } from "../ui/select";
import { ToothPicker } from "./tooth-picker";
import styles from "./calendar.module.css";
import { useBookingScoped } from "./booking-location";

/** The clinicians a booking can name (QA #16): dentists and hygienists. */
const TREATS = new Set(["DENTIST", "HYGIENIST"]);

/**
 * *Edit booking* (QA4-D3, 2026-10-18): what a booked appointment is for, its
 * teeth, who is treating them, and its description. **Not the patient** —
 * moving a booking to another file is cancel and rebook — and not when or
 * where, which is *Reschedule*.
 *
 * A new procedure moves the end to its length, exactly as picking one in the
 * booking form does; the server checks the longer visit against the chair and
 * says so if it does not fit. A one-tooth procedure needs its tooth (QA4-D4).
 */
export function EditBookingDialog({
  appointment,
  codes,
  staff,
  onSaved,
  onClose,
}: {
  appointment: Appointment;
  /** This location's catalogue; a retired procedure the booking already names stays offered. */
  codes: readonly ProcedureCode[];
  staff: readonly StaffMember[];
  onSaved: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const [codeId, setCodeId] = useState(appointment.procedureCodeId ?? "");
  const [teeth, setTeeth] = useState<number[]>([...appointment.fdiNumbers]);
  const [providerId, setProviderId] = useState(appointment.providerId ?? "");
  const [summary, setSummary] = useState(appointment.procedureSummary ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const code = codes.find((c) => c.id === codeId);
  const perTooth = appointment.patient !== null && code?.scope === "PER_TOOTH";
  // Clinicians who work at this location, and whoever the booking already names.
  const providers = staff.filter(
    (person) =>
      person.id === appointment.providerId ||
      (person.isActive &&
        TREATS.has(person.role) &&
        person.memberships.some((m) => m.branchId === appointment.branchId)),
  );
  const ready = !busy && !(perTooth && teeth.length === 0);

  const save = (): void => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    here(editAppointment)(appointment.id, {
      procedureCodeId: codeId === "" ? null : codeId,
      // A whole-mouth procedure has no tooth (TP12), so changing to one clears them.
      fdiNumbers: perTooth ? teeth : [],
      providerId: providerId === "" ? null : providerId,
      procedureSummary: summary.trim() === "" ? null : summary.trim(),
    })
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError || cause instanceof Error
            ? cause.message
            : "Could not save this booking.",
        );
        setBusy(false);
      });
  };

  return (
    <Modal label="Edit booking" onClose={onClose} testId="edit-booking">
      <form
        className={styles.editBooking}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h2 className={styles.toothPickerTitle}>Edit booking</h2>
        {error !== null && (
          <p className={styles.error} role="alert" data-testid="edit-booking-error">
            {error}
          </p>
        )}

        <Select
          id="edit-booking-procedure"
          label="For"
          value={codeId}
          options={codes.map((c) => ({
            value: c.id,
            label: c.label,
            hint: `${String(c.defaultDurationMinutes)} min`,
          }))}
          onChange={setCodeId}
          testId="edit-booking-procedure"
          placeholder="Not from the catalogue"
          emptyLabel="Not from the catalogue"
          note={
            codeId !== (appointment.procedureCodeId ?? "") && code !== undefined
              ? `The visit will last ${String(code.defaultDurationMinutes)} minutes, its length.`
              : undefined
          }
        />

        {perTooth && appointment.patient !== null && (
          <ToothPicker
            patientId={appointment.patient.id}
            value={teeth}
            onChange={setTeeth}
            required
            testId="edit-booking-tooth"
          />
        )}

        <Select
          id="edit-booking-provider"
          label="Who is treating them"
          value={providerId}
          options={providers.map((person) => ({
            value: person.id,
            label: `${person.firstName} ${person.lastName}`,
          }))}
          onChange={setProviderId}
          testId="edit-booking-provider"
          placeholder="Not decided yet"
          emptyLabel="Not decided yet"
        />

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Description</span>
          <input
            className={styles.input}
            value={summary}
            maxLength={200}
            onChange={(event) => setSummary(event.target.value)}
            placeholder="Recall, extraction, maintenance…"
            data-testid="edit-booking-summary"
          />
        </label>

        <div className={styles.toothPickerActions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={!ready}
            data-testid="edit-booking-save"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
